import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CallsTable from "../components/dashboard/CallsTable";
import RetryBlock from "../components/common/RetryBlock";
import TranscriptModal from "../components/modals/TranscriptModal";
import LeadUploadModal from "../components/modals/LeadUploadModal";
import useDebounce from "../hooks/useDebounce";
import useModalState from "../hooks/useModalState";
import useShellContext from "../layout/useShellContext";
import { getDashboardCalls, getDashboardStats } from "../services/dashboardApi";
import { getLeadContextStatus, uploadLeadContext } from "../services/leadsApi";
import { formatDuration, formatNow } from "../utils/formatters";
import { PAGE_SIZE } from "../utils/constants";
import { resolveDateRangeFilter } from "../utils/dateRanges";
import { callKey } from "../utils/helpers";
import { contactName } from "../utils/labels";
import { normalizeApiError } from "../utils/errors";
import PerformanceCard from "../components/dashboard/PerformanceCard";
import DashboardFilterStrip, { resolveDurationBucket } from "../components/dashboard/DashboardFilterStrip";
import DonutMetricCard from "../components/dashboard/DonutMetricCard";
import CallDistributionPanel from "../components/dashboard/CallDistributionPanel";
import CallingAgentSummaryGrid from "../components/calls/CallingAgentSummaryGrid";
import CallingAgentWorkTable from "../components/calls/CallingAgentWorkTable";
import QueueDetailsModal from "../components/modals/QueueDetailsModal";
import UpdateCallStatusModal from "../components/calls/UpdateCallStatusModal";
import { useAuth } from "../context/AuthContext";
import { getCallDetail, getCallingAgentSummary, updateCallWorkflowStatus } from "../services/callsApi";
import { downloadTranscript } from "../utils/callDisplay";
import { isCallingAgentUser } from "../utils/roleAccess";
import {
  getCallHistoryRequestKey,
  groupCallsByPhoneNumber,
  hydrateCallHistoryGroup,
} from "../utils/callHistory";

const initialDashboardData = {
  stats: null,
  leadContextStatus: null,
  calls: [],
  total: 0,
};
const initialCallingAgentSummary = {
  counts: {
    pending_calls: 0,
    assigned_calls: 0,
    completed_followups: 0,
    callback_required: 0,
    today_completed: 0,
  },
  pending_calls: [],
  assigned_calls: [],
  completed_followups: [],
};
const DASHBOARD_GROUPED_CALL_FETCH_LIMIT = 1000;

export default function DashboardPage() {
  const {
    showToast,
    registerPageControls,
    resetPageControls,
    openOutboundModal,
    runBulkCalls,
  } = useShellContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCallingAgent = isCallingAgentUser(user);
  const transcriptModal = useModalState(null);
  const leadUploadModal = useModalState(null);
  const [dashboardData, setDashboardData] = useState(initialDashboardData);
  const [callingAgentSummary, setCallingAgentSummary] = useState(
    initialCallingAgentSummary,
  );
  const [, setSummaryLoading] = useState(true);
  const [callsLoading, setCallsLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [callsError, setCallsError] = useState(null);
  const [callingAgentError, setCallingAgentError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState("--");
  const [shellRefreshing, setShellRefreshing] = useState(false);
  const [callSearchInput, setCallSearchInput] = useState("");
  const [callStatusFilter, setCallStatusFilter] = useState("");
  const [callDirectionFilter, setCallDirectionFilter] = useState("");
  const [callDateRange, setCallDateRange] = useState("30d");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [durationBucket, setDurationBucket] = useState("");
  const [lineFilter, setLineFilter] = useState("");
  const [callsPage, setCallsPage] = useState(0);
  const [selectedCallKeys, setSelectedCallKeys] = useState(new Set());
  const [leadUploadFile, setLeadUploadFile] = useState(null);
  const [leadUploadActivate, setLeadUploadActivate] = useState(false);
  const [leadContextBusy, setLeadContextBusy] = useState(false);
  const [selectedAssignedCall, setSelectedAssignedCall] = useState(null);
  const [statusModalCall, setStatusModalCall] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const debouncedSearch = useDebounce(callSearchInput, 400);
  const debouncedLineFilter = useDebounce(lineFilter, 300);

  const mountedRef = useRef(true);
  const summaryRequestRef = useRef({ id: 0, controller: null });
  const callsRequestRef = useRef({ id: 0, controller: null });
  const didHandleQueryChangeRef = useRef(false);
  const leadSyncTimeoutRef = useRef(null);
  const dashboardQueryRef = useRef({
    search: "",
    status: "",
  });

  const dateFilters = useMemo(
    () => resolveDateRangeFilter(callDateRange, customDateFrom, customDateTo),
    [callDateRange, customDateFrom, customDateTo],
  );
  const durationFilters = useMemo(
    () => resolveDurationBucket(durationBucket),
    [durationBucket],
  );

  dashboardQueryRef.current = {
    search: debouncedSearch,
    status: callStatusFilter,
    direction: callDirectionFilter,
    date_from: dateFilters.date_from,
    date_to: dateFilters.date_to,
    duration_min: durationFilters.duration_min,
    duration_max: durationFilters.duration_max,
    line: debouncedLineFilter,
  };

  const abortSummaryRequest = useCallback(() => {
    if (summaryRequestRef.current.controller) {
      summaryRequestRef.current.controller.abort();
      summaryRequestRef.current.controller = null;
    }
  }, []);

  const abortCallsRequest = useCallback(() => {
    if (callsRequestRef.current.controller) {
      callsRequestRef.current.controller.abort();
      callsRequestRef.current.controller = null;
    }
  }, []);

  const abortAllRequests = useCallback(() => {
    abortSummaryRequest();
    abortCallsRequest();
  }, [abortCallsRequest, abortSummaryRequest]);

  const loadDashboardData = useCallback(
    async ({
      includeSummary = true,
      includeCalls = true,
      queryOverride,
      trackRefresh = false,
    } = {}) => {
      if (isCallingAgent) {
        return undefined;
      }

      if (!includeSummary && !includeCalls) {
        return undefined;
      }

      const query = queryOverride || dashboardQueryRef.current;
      let firstError = null;
      let hadSuccess = false;

      if (includeSummary) {
        abortSummaryRequest();
        setSummaryLoading(true);
        setSummaryError(null);
      }

      if (includeCalls) {
        abortCallsRequest();
        setCallsLoading(true);
        setCallsError(null);
      }

      if (trackRefresh) {
        setShellRefreshing(true);
      }

      try {
        const tasks = [];

        if (includeSummary) {
          const controller = new AbortController();
          const requestId = summaryRequestRef.current.id + 1;
          summaryRequestRef.current = {
            id: requestId,
            controller,
          };

          tasks.push(
            Promise.all([
              getDashboardStats({
                signal: controller.signal,
                query,
              }),
              getLeadContextStatus({ signal: controller.signal }),
            ])
              .then(([stats, leadContextStatus]) => {
                if (
                  !mountedRef.current ||
                  controller.signal.aborted ||
                  summaryRequestRef.current.id !== requestId
                ) {
                  return;
                }

                hadSuccess = true;
                setDashboardData((current) => ({
                  ...current,
                  stats,
                  leadContextStatus,
                }));
                setSummaryError(null);
              })
              .catch((error) => {
                const normalizedError = normalizeApiError(error);
                if (
                  !normalizedError.isAbort &&
                  mountedRef.current &&
                  summaryRequestRef.current.id === requestId
                ) {
                  setSummaryError(normalizedError);
                  firstError = firstError || normalizedError;
                }
                return normalizedError;
              })
              .finally(() => {
                if (
                  mountedRef.current &&
                  summaryRequestRef.current.id === requestId
                ) {
                  setSummaryLoading(false);
                  summaryRequestRef.current.controller = null;
                }
              }),
          );
        }

        if (includeCalls) {
          const controller = new AbortController();
          const requestId = callsRequestRef.current.id + 1;
          callsRequestRef.current = {
            id: requestId,
            controller,
          };

          tasks.push(
            getDashboardCalls(
              {
                limit: DASHBOARD_GROUPED_CALL_FETCH_LIMIT,
                offset: 0,
                search: query.search,
                status: query.status,
                direction: query.direction,
                date_from: query.date_from,
                date_to: query.date_to,
              },
              { signal: controller.signal },
            )
              .then((response) => {
                if (
                  !mountedRef.current ||
                  controller.signal.aborted ||
                  callsRequestRef.current.id !== requestId
                ) {
                  return;
                }

                const rawCalls = Array.isArray(response)
                  ? response
                  : response?.calls || [];
                hadSuccess = true;
                setDashboardData((current) => ({
                  ...current,
                  calls: rawCalls.map((call, index) => ({
                    ...call,
                    __key: callKey(call, index),
                  })),
                  total: response?.total || rawCalls.length,
                }));
                setCallsError(null);
              })
              .catch((error) => {
                const normalizedError = normalizeApiError(error);
                if (
                  !normalizedError.isAbort &&
                  mountedRef.current &&
                  callsRequestRef.current.id === requestId
                ) {
                  setCallsError(normalizedError);
                  firstError = firstError || normalizedError;
                }
                return normalizedError;
              })
              .finally(() => {
                if (
                  mountedRef.current &&
                  callsRequestRef.current.id === requestId
                ) {
                  setCallsLoading(false);
                  callsRequestRef.current.controller = null;
                }
              }),
          );
        }

        await Promise.allSettled(tasks);

        if (mountedRef.current && hadSuccess) {
          setLastUpdated(formatNow());
        }

        if (firstError) {
          throw firstError;
        }

        return undefined;
      } finally {
        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [abortCallsRequest, abortSummaryRequest, isCallingAgent],
  );

  const loadCallingAgentDashboard = useCallback(
    async ({ trackRefresh = false } = {}) => {
      if (!isCallingAgent) {
        return undefined;
      }

      if (trackRefresh && mountedRef.current) {
        setShellRefreshing(true);
      }

      setCallsLoading(true);
      setCallingAgentError(null);

      try {
        const summary = await getCallingAgentSummary();

        if (!mountedRef.current) {
          return undefined;
        }

        setCallingAgentSummary({
          ...initialCallingAgentSummary,
          ...summary,
          counts: {
            ...initialCallingAgentSummary.counts,
            ...(summary?.counts || {}),
          },
        });
        setLastUpdated(formatNow());
        return undefined;
      } catch (requestError) {
        const normalizedError = normalizeApiError(requestError);

        if (!normalizedError.isAbort && mountedRef.current) {
          setCallingAgentError(normalizedError);
        }

        throw normalizedError;
      } finally {
        if (mountedRef.current) {
          setCallsLoading(false);
        }

        if (trackRefresh && mountedRef.current) {
          setShellRefreshing(false);
        }
      }
    },
    [isCallingAgent],
  );

  const handleRefresh = useCallback(
    () =>
      isCallingAgent
        ? loadCallingAgentDashboard({ trackRefresh: true })
        : loadDashboardData({
            includeSummary: true,
            includeCalls: true,
            trackRefresh: true,
          }),
    [isCallingAgent, loadCallingAgentDashboard, loadDashboardData],
  );

  useEffect(() => {
    mountedRef.current = true;

    if (isCallingAgent) {
      loadCallingAgentDashboard().catch(() => undefined);
    } else {
      loadDashboardData({
        includeSummary: true,
        includeCalls: true,
      }).catch(() => undefined);
    }

    return () => {
      mountedRef.current = false;
      abortAllRequests();

      if (leadSyncTimeoutRef.current) {
        window.clearTimeout(leadSyncTimeoutRef.current);
        leadSyncTimeoutRef.current = null;
      }
    };
  }, [abortAllRequests, isCallingAgent, loadCallingAgentDashboard, loadDashboardData]);

  useEffect(() => {
    if (isCallingAgent) {
      return;
    }

    if (!didHandleQueryChangeRef.current) {
      didHandleQueryChangeRef.current = true;
      return;
    }

    loadDashboardData({
      includeSummary: true,
      includeCalls: true,
      queryOverride: {
        search: debouncedSearch,
        status: callStatusFilter,
        direction: callDirectionFilter,
        date_from: dateFilters.date_from,
        date_to: dateFilters.date_to,
        duration_min: durationFilters.duration_min,
        duration_max: durationFilters.duration_max,
        line: debouncedLineFilter,
      },
    }).catch(() => undefined);
  }, [
    callDirectionFilter,
    callStatusFilter,
    dateFilters.date_from,
    dateFilters.date_to,
    debouncedLineFilter,
    debouncedSearch,
    durationFilters.duration_min,
    durationFilters.duration_max,
    isCallingAgent,
    loadDashboardData,
  ]);

  useEffect(() => {
    registerPageControls({
      onRefresh: handleRefresh,
      refreshEvery: "Manual",
      lastUpdated,
      isRefreshing: shellRefreshing,
    });

    return () => {
      resetPageControls();
    };
  }, [
    handleRefresh,
    lastUpdated,
    registerPageControls,
    resetPageControls,
    shellRefreshing,
  ]);

  const buildHistoryUrl = useCallback(
    (extraFilters = {}) => {
      const params = new URLSearchParams();
      params.set("tab", "history");

      const nextRange = extraFilters.range ?? callDateRange;
      if (nextRange) {
        params.set("range", nextRange);
      }
      if (nextRange === "custom") {
        if (customDateFrom) {
          params.set("date_from", customDateFrom);
        }
        if (customDateTo) {
          params.set("date_to", customDateTo);
        }
      }
      if (extraFilters.direction) {
        params.set("direction", extraFilters.direction);
      }
      if (extraFilters.status) {
        params.set("status", extraFilters.status);
      }
      if (extraFilters.line || lineFilter) {
        params.set("line", extraFilters.line || lineFilter);
      }
      if (extraFilters.duration_min || durationFilters.duration_min) {
        params.set("duration_min", extraFilters.duration_min || durationFilters.duration_min);
      }
      if (extraFilters.duration_max || durationFilters.duration_max) {
        params.set("duration_max", extraFilters.duration_max || durationFilters.duration_max);
      }

      return `/calls?${params.toString()}`;
    },
    [callDateRange, customDateFrom, customDateTo, durationFilters.duration_max, durationFilters.duration_min, lineFilter],
  );

  const stats = dashboardData.stats || {};
  const totalCalls = Number(stats.total_calls || 0);
  const totalBookings = Number(stats.total_bookings || stats.booked_calls || 0);
  const requestedBookings = Number(stats.total_booking_requests || stats.requested_calls || 0);
  const inboundCalls = Number(stats.inbound_calls || 0);
  const outboundCalls = Number(stats.outbound_calls || 0);
  const droppedCalls = Number(stats.dropped_calls || 0);
  const bookingRate = Number(stats.booking_rate || 0);
  const callsWithDuration = Number(stats.calls_with_duration || 0);
  const avgDuration = Number(stats.avg_talk_time_seconds || stats.avg_duration || 0);
  const groupedCalls = useMemo(
    () => groupCallsByPhoneNumber(dashboardData.calls || []),
    [dashboardData.calls],
  );
  const callsTotal = groupedCalls.length;
  const calls = useMemo(() => {
    const startIndex = callsPage * PAGE_SIZE;
    return groupedCalls.slice(startIndex, startIndex + PAGE_SIZE);
  }, [callsPage, groupedCalls]);
  const performanceData = dashboardData.stats?.performance || {};

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(callsTotal / PAGE_SIZE) - 1);
    setCallsPage((current) => (current > maxPage ? maxPage : current));
  }, [callsTotal]);

  const handleOpenCallDetails = useCallback(
    async (call) => {
      const modalRequestKey = getCallHistoryRequestKey(call);

      transcriptModal.open({
        ...call,
        __modalRequestKey: modalRequestKey,
      });

      const detailedGroup = await hydrateCallHistoryGroup(call, getCallDetail);

      if (!detailedGroup) {
        return;
      }

      transcriptModal.update((currentCall) => {
        const currentKey =
          currentCall?.__modalRequestKey ||
          currentCall?.phoneGroupKey ||
          currentCall?.__groupKey ||
          currentCall?.call_id ||
          currentCall?.id;

        if (currentKey !== modalRequestKey) {
          return currentCall;
        }

        return {
          ...detailedGroup,
          __modalRequestKey: modalRequestKey,
        };
      });
    },
    [transcriptModal],
  );

  const handleOpenAssignedCall = useCallback((call) => {
    setSelectedAssignedCall(call);
  }, []);

  const handleDirectAssignedCall = useCallback(
    (call) => {
      openOutboundModal({
        phone: call.phone || call.phone_number || "",
        name: call.patient_name || call.caller_name || "",
        reason: call.service_type || call.reason || "follow-up",
        summary: call.summary || "",
      });
    },
    [openOutboundModal],
  );

  const callingAgentCounts = callingAgentSummary?.counts || initialCallingAgentSummary.counts;

  if (isCallingAgent) {
    return (
      <>
        <CallingAgentSummaryGrid counts={callingAgentCounts} />

        {callingAgentError ? (
          <RetryBlock
            message="Assigned follow-up data is unavailable right now."
            onRetry={() => loadCallingAgentDashboard({ trackRefresh: true }).catch(() => undefined)}
          />
        ) : null}

        <CallingAgentWorkTable
          title="Pending Calls"
          items={callingAgentSummary.pending_calls || []}
          loading={callsLoading}
          emptyTitle="No pending calls found"
          emptySubtitle="Assigned follow-up work waiting on you will appear here."
          onView={handleOpenAssignedCall}
          onDirectCall={handleDirectAssignedCall}
          onUpdateStatus={setStatusModalCall}
        />

        <CallingAgentWorkTable
          title="Assigned Calls"
          items={callingAgentSummary.assigned_calls || []}
          loading={callsLoading}
          emptyTitle="No assigned calls found"
          emptySubtitle="Active work in progress will appear here."
          onView={handleOpenAssignedCall}
          onDirectCall={handleDirectAssignedCall}
          onUpdateStatus={setStatusModalCall}
        />

        <CallingAgentWorkTable
          title="Completed Follow-ups"
          items={callingAgentSummary.completed_followups || []}
          loading={callsLoading}
          emptyTitle="No completed follow-ups yet"
          emptySubtitle="Completed follow-up work will appear here after status updates."
          onView={handleOpenAssignedCall}
          onDirectCall={handleDirectAssignedCall}
          onUpdateStatus={setStatusModalCall}
        />

        <QueueDetailsModal
          open={Boolean(selectedAssignedCall)}
          queueCall={selectedAssignedCall}
          onClose={() => setSelectedAssignedCall(null)}
        />

        <UpdateCallStatusModal
          open={Boolean(statusModalCall)}
          busy={statusBusy}
          queueCall={statusModalCall}
          onClose={() => {
            if (!statusBusy) {
              setStatusModalCall(null);
            }
          }}
          onSubmit={async (payload) => {
            if (!statusModalCall?.call_id) {
              return;
            }

            setStatusBusy(true);

            try {
              await updateCallWorkflowStatus(statusModalCall.call_id, payload);
              showToast("Call status updated successfully.", false, 4000);
              setStatusModalCall(null);
              await loadCallingAgentDashboard({ trackRefresh: true });
            } catch (requestError) {
              const normalizedError = normalizeApiError(requestError);

              if (!normalizedError.isAbort) {
                showToast(normalizedError.message, true, 5000);
              }
            } finally {
              setStatusBusy(false);
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <DashboardFilterStrip
        searchValue={callSearchInput}
        onSearchChange={(value) => {
          setCallSearchInput(value);
          setCallsPage(0);
        }}
        statusFilter={callStatusFilter}
        onStatusFilterChange={(value) => {
          setCallStatusFilter(value);
          setCallsPage(0);
        }}
        directionFilter={callDirectionFilter}
        onDirectionFilterChange={(value) => {
          setCallDirectionFilter(value);
          setCallsPage(0);
        }}
        dateRange={callDateRange}
        onDateRangeChange={(value) => {
          setCallDateRange(value);
          setCallsPage(0);
        }}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        onCustomDateFromChange={(value) => {
          setCustomDateFrom(value);
          setCallsPage(0);
        }}
        onCustomDateToChange={(value) => {
          setCustomDateTo(value);
          setCallsPage(0);
        }}
        durationBucket={durationBucket}
        onDurationBucketChange={(value) => {
          setDurationBucket(value);
          setCallsPage(0);
        }}
        lineFilter={lineFilter}
        onLineFilterChange={(value) => {
          setLineFilter(value);
          setCallsPage(0);
        }}
        onClear={() => {
          setCallSearchInput("");
          setCallStatusFilter("");
          setCallDirectionFilter("");
          setCallDateRange("30d");
          setCustomDateFrom("");
          setCustomDateTo("");
          setDurationBucket("");
          setLineFilter("");
          setCallsPage(0);
        }}
      />

      <section className="dashboard-kpi-grid" aria-label="Dashboard KPI cards">
        <DonutMetricCard
          title="Booking Rate"
          value={totalCalls > 0 ? `${Math.round(bookingRate)}%` : "--"}
          subtitle={`${totalBookings.toLocaleString()} booked / ${totalCalls.toLocaleString()} total`}
          footer="Booked calls divided by total calls"
          onClick={() => navigate(buildHistoryUrl({ status: "booked" }))}
          tone="booking"
        />
        <DonutMetricCard
          title="Total Calls"
          value={totalCalls.toLocaleString()}
          subtitle={`${inboundCalls.toLocaleString()} inbound / ${outboundCalls.toLocaleString()} outbound`}
          footer={droppedCalls ? `${droppedCalls.toLocaleString()} dropped` : "No dropped calls"}
          onClick={() => navigate(buildHistoryUrl({}))}
        />
        <DonutMetricCard
          title="Appointments"
          value={totalBookings.toLocaleString()}
          subtitle={requestedBookings ? `${requestedBookings.toLocaleString()} requested` : "confirmed"}
          footer="Confirmed appointment outcomes"
          onClick={() => navigate("/appointments")}
          tone="appointments"
        />
        <DonutMetricCard
          title="Avg Duration"
          value={callsWithDuration > 0 || avgDuration > 0 ? formatDuration(avgDuration) : "--"}
          subtitle={callsWithDuration ? `${callsWithDuration.toLocaleString()} calls` : "No duration data"}
          footer="Average connected talk time"
          onClick={() => navigate(buildHistoryUrl({}))}
          tone="duration"
        />
      </section>

      <div className="dashboard-main-row">
        <CallDistributionPanel
          stats={stats}
          onNavigate={(filters) => navigate(buildHistoryUrl(filters))}
        />
        <PerformanceCard
          chartData={performanceData.chartData}
        />
      </div>

      {summaryError && dashboardData.stats ? (
        <RetryBlock
          message="Stats are unavailable right now. Showing the latest loaded values."
          onRetry={() =>
            loadDashboardData({
              includeSummary: true,
              includeCalls: false,
            }).catch(() => undefined)
          }
        />
      ) : null}

      {callsError && calls.length ? (
        <RetryBlock
          message="Recent call data is unavailable right now."
          onRetry={() =>
            loadDashboardData({
              includeSummary: false,
              includeCalls: true,
            }).catch(() => undefined)
          }
        />
      ) : null}

      <CallsTable
        calls={calls}
        total={callsTotal}
        currentPage={callsPage}
        searchValue={callSearchInput}
        onSearchChange={(value) => {
          setCallSearchInput(value);
          setCallsPage(0);
        }}
        statusFilter={callStatusFilter}
        onStatusFilterChange={(value) => {
          setCallStatusFilter(value);
          setCallsPage(0);
        }}
        directionFilter={callDirectionFilter}
        onDirectionFilterChange={(value) => {
          setCallDirectionFilter(value);
          setCallsPage(0);
        }}
        dateRange={callDateRange}
        onDateRangeChange={(value) => {
          setCallDateRange(value);
          setCallsPage(0);
        }}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        onCustomDateFromChange={(value) => {
          setCustomDateFrom(value);
          setCallsPage(0);
        }}
        onCustomDateToChange={(value) => {
          setCustomDateTo(value);
          setCallsPage(0);
        }}
        onScheduleCall={() => openOutboundModal()}
        selectedIds={selectedCallKeys}
        onToggleId={(id, checked) =>
          setSelectedCallKeys((current) => {
            const next = new Set(current);

            if (checked) {
              next.add(id);
            } else {
              next.delete(id);
            }

            return next;
          })
        }
        onToggleAll={(checked) =>
          setSelectedCallKeys(
            checked
              ? new Set(
                  calls
                    .filter((call) => call.phone_number)
                    .map((call) => call.__groupKey || call.__key),
                )
              : new Set(),
          )
        }
        onPrevPage={() => setCallsPage((current) => Math.max(0, current - 1))}
        onNextPage={() => setCallsPage((current) => current + 1)}
        onOpenTranscript={handleOpenCallDetails}
        onDownloadTranscript={(call) => {
          if (!downloadTranscript(call)) {
            showToast("No transcript available", true, 4000);
          }
        }}
        showFilters={false}
        loading={callsLoading}
      />

      {selectedCallKeys.size > 0 ? (
        <div className="inline-selection-bar">
          <span>{selectedCallKeys.size} selected</span>
          <div className="page-actions">
            <button
              type="button"
              className="btn compact-btn"
              onClick={() => setSelectedCallKeys(new Set())}
            >
              Clear Selection
            </button>
            <button
              type="button"
              className="btn btn-gold compact-btn"
              onClick={async () => {
                const targets = calls
                  .filter(
                    (call) =>
                      selectedCallKeys.has(call.__groupKey || call.__key) &&
                      call.phone_number,
                  )
                  .map((call) => ({
                    phone: call.phone_number,
                    name: contactName(call.caller_name),
                    reason: "follow-up",
                  }));

                const result = await runBulkCalls(targets);

                if (!result.cancelled) {
                  setSelectedCallKeys(new Set());
                  loadDashboardData({
                    includeSummary: true,
                    includeCalls: true,
                  }).catch(() => undefined);
                }
              }}
            >
              Call Selected
            </button>
          </div>
        </div>
      ) : null}

      <TranscriptModal
        open={transcriptModal.isOpen}
        call={transcriptModal.data}
        showCallMetadata
        onDownloadTranscript={(call) => {
          if (!downloadTranscript(call)) {
            showToast("No transcript available", true, 4000);
          }
        }}
        onClose={transcriptModal.close}
        onCallAgain={(phone, name) => {
          transcriptModal.close();
          openOutboundModal({
            phone,
            name,
            reason: "follow-up",
            summary: "",
          });
        }}
      />

      <LeadUploadModal
        open={leadUploadModal.isOpen}
        file={leadUploadFile}
        activate={leadUploadActivate}
        busy={leadContextBusy}
        onFileChange={setLeadUploadFile}
        onActivateChange={setLeadUploadActivate}
        onClose={() => {
          if (!leadContextBusy) {
            leadUploadModal.close();
            setLeadUploadFile(null);
            setLeadUploadActivate(false);
          }
        }}
        onSubmit={async (event) => {
          event.preventDefault();

          if (!leadUploadFile) {
            showToast("Choose a file first.", true);
            return;
          }

          const formData = new FormData();
          formData.append("file", leadUploadFile);
          formData.append("activate", leadUploadActivate ? "true" : "false");
          setLeadContextBusy(true);

          try {
            const response = await uploadLeadContext(formData);
            setDashboardData((current) => ({
              ...current,
              leadContextStatus: response?.status || current.leadContextStatus,
            }));
            showToast(
              `${response?.lead_count || 0} leads loaded.`,
              false,
              5000,
            );
            leadUploadModal.close();
            setLeadUploadFile(null);
            setLeadUploadActivate(false);
          } catch (requestError) {
            const normalizedError = normalizeApiError(requestError);
            if (!normalizedError.isAbort) {
              showToast(normalizedError.message, true, 7000);
            }
          } finally {
            setLeadContextBusy(false);
          }
        }}
      />
    </>
  );
}
